
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Calculator, TrendingUp, DollarSign, Percent, Check } from "lucide-react";
import ConsumerHeader from "@/components/ConsumerHeader";

const RevenueCalculator = () => {
  const [sessionsPerDay, setSessionsPerDay] = useState([9]);
  const [daysPerMonth, setDaysPerMonth] = useState([25]);
  const [occupancyPercent, setOccupancyPercent] = useState([30]);
  const [roomCapacity, setRoomCapacity] = useState([4]);
  
  const sessionPrice = 20.00;
  const platformFeePercent = 5;
  const stripeFeePercent = 1.5;
  const stripeFeeFixed = 0.20;
  const monthlySoftwareCost = 49;

  // Calculate revenue and fees with occupancy and room capacity
  const potentialSessions = sessionsPerDay[0] * daysPerMonth[0] * roomCapacity[0];
  const actualSessions = Math.round(potentialSessions * (occupancyPercent[0] / 100));
  const grossRevenue = actualSessions * sessionPrice;
  
  // Platform fee (5%)
  const platformFee = grossRevenue * (platformFeePercent / 100);
  
  // Net revenue after platform fee
  const netAfterPlatform = grossRevenue - platformFee;
  
  // Stripe fee (1.5% + 20p per transaction)
  const stripeFeeVariable = netAfterPlatform * (stripeFeePercent / 100);
  const stripeFeeFixedTotal = actualSessions * stripeFeeFixed;
  const totalStripeFee = stripeFeeVariable + stripeFeeFixedTotal;
  
  // Net revenue after Stripe fees (this is the final net revenue during beta)
  const finalNetRevenue = netAfterPlatform - totalStripeFee;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Calculator className="h-16 w-16 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Revenue Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Calculate your potential monthly revenue and understand the associated fees when using Wellsuites
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5" />
                <span>Business Parameters</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Average Session Price
                  </label>
                  <span className="text-lg font-semibold text-blue-600">
                    {formatCurrency(sessionPrice)}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  Based on industry averages for contrast therapy
                </p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Room Capacity
                  </label>
                  <span className="text-lg font-semibold text-blue-600">
                    {roomCapacity[0]} people
                  </span>
                </div>
                <Slider
                  value={roomCapacity}
                  onValueChange={setRoomCapacity}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span>
                  <span>10</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Maximum number of people that can use the facilities simultaneously
                </p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Sessions per Day
                  </label>
                  <span className="text-lg font-semibold text-blue-600">
                    {sessionsPerDay[0]}
                  </span>
                </div>
                <Slider
                  value={sessionsPerDay}
                  onValueChange={setSessionsPerDay}
                  max={14}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span>
                  <span>14</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Working Days per Month
                  </label>
                  <span className="text-lg font-semibold text-blue-600">
                    {daysPerMonth[0]}
                  </span>
                </div>
                <Slider
                  value={daysPerMonth}
                  onValueChange={setDaysPerMonth}
                  max={31}
                  min={10}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>10</span>
                  <span>31</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Occupancy Rate
                  </label>
                  <span className="text-lg font-semibold text-blue-600">
                    {occupancyPercent[0]}%
                  </span>
                </div>
                <Slider
                  value={occupancyPercent}
                  onValueChange={setOccupancyPercent}
                  max={100}
                  min={10}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>10%</span>
                  <span>100%</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Percentage of available time slots that get booked
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-900">Monthly Summary</span>
                </div>
                <p className="text-sm text-blue-700 mb-1">
                  {sessionsPerDay[0]} sessions × {daysPerMonth[0]} days × {roomCapacity[0]} capacity
                </p>
                <p className="text-sm text-blue-700 mb-1">
                  = {potentialSessions} potential sessions × {occupancyPercent[0]}% occupancy
                </p>
                <p className="text-sm text-blue-700">
                  = {actualSessions} actual sessions at {formatCurrency(sessionPrice)} each
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Percent className="h-5 w-5" />
                <span>Revenue Breakdown</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Gross Revenue */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-900">
                    Gross Revenue
                  </span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(grossRevenue)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {actualSessions} sessions × {formatCurrency(sessionPrice)}
                </p>
              </div>

              {/* Platform Fee */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">
                    Wellsuites Platform Fee ({platformFeePercent}%)
                  </span>
                  <span className="text-red-600 font-medium">
                    -{formatCurrency(platformFee)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Connect fee for using the platform
                </p>
                
                {/* Platform Fee Includes Section */}
                <div className="mt-4 bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Platform fee includes:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">Advanced booking system</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">Memberships management</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">Integration with self service entry system</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">Pre-heat sauna integration <span className="text-xs italic">*requires smart hardware</span></span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">SMS reminders</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">24/7 live chat and phone support for customers</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stripe Fees */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">
                    Stripe Processing Fees
                  </span>
                  <span className="text-red-600 font-medium">
                    -{formatCurrency(totalStripeFee)}
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-1 space-y-1">
                  <p>{stripeFeePercent}% variable: {formatCurrency(stripeFeeVariable)}</p>
                  <p>20p × {actualSessions} transactions: {formatCurrency(stripeFeeFixedTotal)}</p>
                </div>
              </div>

              {/* Monthly Software Cost */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">
                    Monthly Software Cost
                  </span>
                  <span className="text-gray-400 font-medium line-through">
                    -{formatCurrency(monthlySoftwareCost)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Software subscription fee
                </p>
                
                {/* Beta Information */}
                <div className="mt-4 bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">🎉 Beta Phase Special</h4>
                  <p className="text-sm text-green-800">
                    <span className="font-medium">FREE for the first 3 months</span> as we're currently in beta phase!
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Regular pricing applies after the beta period
                  </p>
                </div>
              </div>

              {/* Net Revenue */}
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-green-900">
                    Your Net Revenue
                  </span>
                  <span className="text-3xl font-bold text-green-600">
                    {formatCurrency(finalNetRevenue)}
                  </span>
                </div>
                <p className="text-sm text-green-700 mt-2">
                  After all fees and charges
                </p>
                <p className="text-xs text-green-600 mt-1">
                  *Software cost of {formatCurrency(monthlySoftwareCost)} is FREE during your 3-month beta period
                </p>
                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Effective rate:</span> {" "}
                    {((finalNetRevenue / grossRevenue) * 100).toFixed(1)}% of gross revenue
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Information */}
        <Card className="mt-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">No Setup Fees</h4>
                <p className="text-sm text-gray-600">
                  Get started immediately with no upfront costs
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Transparent Pricing</h4>
                <p className="text-sm text-gray-600">
                  Simple, clear fee structure with no hidden charges
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Weekly Payouts</h4>
                <p className="text-sm text-gray-600">
                  Receive payments directly to your bank account
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RevenueCalculator;
